import { ClientBrandingStorage } from '@/types/storage';
import { resolveBrandingStylesFetchUrl } from '@/utils/brandingStylesUrl';

const CLIENT_BRANDING_STORAGE_KEY = 'client_branding';

export class ClientBrandingHelper {
  /**
   * Get client branding from localStorage
   * @returns ClientBrandingStorage | null
   */
  static getClientBranding(): ClientBrandingStorage | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      const storedBranding = localStorage.getItem(CLIENT_BRANDING_STORAGE_KEY);
      if (!storedBranding) {
        return null;
      }

      return JSON.parse(storedBranding) as ClientBrandingStorage;
    } catch (error) {
      console.error('Error getting client branding from localStorage:', error);
      return null;
    }
  }

  /**
   * Save client branding in localStorage
   * @param branding ClientBrandingStorage
   */
  static saveClientBranding(branding: ClientBrandingStorage): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      localStorage.setItem(
        CLIENT_BRANDING_STORAGE_KEY,
        JSON.stringify(branding),
      );
    } catch (error) {
      console.error('Error setting client branding in localStorage:', error);
    }
  }

  /**
   * Update specific client branding in localStorage
   * @param updates Partial<ClientBrandingStorage>
   */
  static updateClientBranding(updates: Partial<ClientBrandingStorage>): void {
    try {
      const currentBranding = this.getClientBranding();
      if (!currentBranding) {
        console.warn(
          'No existing client branding found. Use saveClientBranding instead.',
        );
        return;
      }

      const updatedBranding = { ...currentBranding, ...updates };
      this.saveClientBranding(updatedBranding);
    } catch (error) {
      console.error('Error updating client branding:', error);
    }
  }

  /**
   * Clear client branding from localStorage
   */
  static clearClientBranding(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      localStorage.removeItem(CLIENT_BRANDING_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing client branding from localStorage:', error);
    }
  }

  /**
   * Clear client branding from localStorage
   */
  static async fetchStylesVersion() {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      const branding = this.getClientBranding();
      if (!branding?.branding.stylesVersion) {
        return;
      }
      const response = await fetch(
        resolveBrandingStylesFetchUrl(branding?.branding.stylesVersion),
        {
          credentials: 'omit',
          mode: 'cors',
        },
      );
      console.log(response);
      return response.json();
    } catch (error) {
      console.error('Error clearing client branding from localStorage:', error);
    }
  }
}
